import React from 'react';
import {
	FormattedMessage,
} from 'react-intl';

import type CK from '~/types/contentking';

import GeneralAdminLink from '~/components/app/GeneralAdminLink';
import LogoutMenuItem from '~/components/app/LogoutMenuItem';
import PlaceholderLink from '~/components/app/PlaceholderLink';
import WebsiteSettingsLink from '~/components/app/WebsiteSettingsLink';
import YourProfileLink from '~/components/app/YourProfileLink';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import ExpandableNavSection from '~/components/patterns/navigations/ExpandableNavSection';
import NavigationIcon, {
	NavigationIconType,
} from '~/components/patterns/icons/NavigationIcon';

import {
	type messages
} from '../MainNavigation';



type Props = {
	accountId: CK.AccountId | null,
	collapseAllExpandables?: () => void,
	collapseExpandablesExceptAccount?: () => void,
	effectiveHomeAccountId: CK.AccountId | null,
	hasAccountActiveDescendant?: boolean,
	hasInternalActiveDescendant?: boolean,
	isAccountExpanded?: boolean,
	isInternalExpanded?: boolean,
	kingdomAdminFeatures?: {
		areAlwaysVisible?: boolean,
		areVisible?: boolean,
	},
	messages: typeof messages,
	onAccountPillToggle?: () => void,
	onAccountToggle?: () => void,
	onInternalPillToggle?: () => void,
	onInternalToggle?: () => void,
	openSwitchAccountModal?: () => void,
	showIntegrations?: boolean,
	websiteId: CK.WebsiteId | null,
	/** Green main header only after Account main-zone expand+navigate, not pill-only toggle */
	expandNavigatePrimaryGreenAccount?: boolean,
	/** Same for Internal */
	expandNavigatePrimaryGreenInternal?: boolean,
};

/**
 * Build secondary navigation items (Integrations, Settings, Account)
 *
 * Returns an array of JSX elements for the secondary navigation section
 */
export function buildNewSecondaryItems(props: Props): Array<React.ReactElement> {
	const {
		accountId,
		collapseAllExpandables,
		collapseExpandablesExceptAccount,
		effectiveHomeAccountId,
		hasAccountActiveDescendant = false,
		hasInternalActiveDescendant = false,
		isAccountExpanded = false,
		isInternalExpanded = false,
		kingdomAdminFeatures,
		messages,
		onAccountPillToggle,
		onAccountToggle,
		onInternalPillToggle,
		onInternalToggle,
		openSwitchAccountModal,
		showIntegrations = false,
		websiteId,
		expandNavigatePrimaryGreenAccount = false,
		expandNavigatePrimaryGreenInternal = false,
	} = props;

	const showInternal = kingdomAdminFeatures?.areAlwaysVisible === true;

	const items: Array<React.ReactElement> = [
		// Integrations (SSO only)
		...(showIntegrations ? [
			<li
				className="main-nav__item"
				data-secondary-nav-item="true"
				key="integrations-item"
			>
				<PlaceholderLink
					onClick={collapseAllExpandables}
					sectionName="integrations"
				>
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={(
									<NavigationIcon
										size={20}
										type={NavigationIconType.Integrations}
									/>
								)}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.integrations} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>,
		] : []),
		// Settings
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="settings-item"
		>
			<WebsiteSettingsLink
				onClick={collapseAllExpandables}
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => (
					<MenuItemLayout
						icon={(
							<NavigationIcon
								size={20}
								type={NavigationIconType.Settings}
							/>
						)}
						isActive={isActive}
						isDimmed={isDimmed}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.settings} />
						)}
					/>
				)}
			</WebsiteSettingsLink>
		</li>,
		// Account (collapsible): EXACT same structure as Technical - button, no isActive, no Link
		<ExpandableNavSection
			dataAttributes={{ 'data-secondary-nav-item': 'true' }}
			expandNavigatePrimaryGreen={expandNavigatePrimaryGreenAccount}
			hasActiveDescendant={hasAccountActiveDescendant}
			icon={(
				<NavigationIcon
					size={20}
					type={NavigationIconType.Account}
				/>
			)}
			isExpanded={isAccountExpanded}
			isSubItemsHidden={false}
			key="account-section"
			label={(
				<FormattedMessage {...messages.account} />
			)}
			onPillToggle={onAccountPillToggle}
			onToggle={onAccountToggle ?? (() => {})}
		>
			{/* Manage My Profile - keep Account expanded when navigating */}
			<li
				className="main-nav__item"
				key="account-manage-profile-item"
			>
				<YourProfileLink
					accountId={accountId ?? effectiveHomeAccountId ?? undefined}
					onClick={collapseExpandablesExceptAccount ?? collapseAllExpandables}
				>
					{({ isActive, isDimmed }) => (
						<MenuItemLayout
							icon={<></>}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.manageMyProfile} />
							)}
						/>
					)}
				</YourProfileLink>
			</li>
			{/* Log out */}
			<li
				className="main-nav__item"
				key="account-logout-item"
			>
				<LogoutMenuItem
					isIconHidden={true}
					isLabelHidden={false}
				/>
			</li>
		</ExpandableNavSection>,
	];

	// Internal (collapsible) - only shown for kingdom admins when admin features are visible
	// EXACT same structure as Technical - button, no isActive, no Link
	if (showInternal && onInternalToggle && openSwitchAccountModal) {
		items.push(
			<ExpandableNavSection
				dataAttributes={{
					'data-secondary-nav-item': 'true',
					'data-internal-section': 'true',
				}}
				expandNavigatePrimaryGreen={expandNavigatePrimaryGreenInternal}
				hasActiveDescendant={hasInternalActiveDescendant}
				icon={(
					<NavigationIcon
						size={20}
						type={NavigationIconType.LockInSquare}
					/>
				)}
				isExpanded={isInternalExpanded}
				isSubItemsHidden={false}
				key="internal-section"
				label={(
					<FormattedMessage {...messages.internal} />
				)}
				onPillToggle={onInternalPillToggle}
				onToggle={onInternalToggle}
			>
				{/* Switch Account */}
				<li
					className="main-nav__item"
					key="internal-switch-account-item"
				>
					<MenuItemLayout
						icon={<></>}
						isActive={false}
						isDimmed={false}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.switchAccount} />
						)}
						onClickCallback={() => openSwitchAccountModal()}
					/>
				</li>
				{/* General Admin */}
				<li
					className="main-nav__item"
					key="internal-general-admin-item"
				>
					<GeneralAdminLink>
						{({ isActive, isDimmed }) => (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.generalAdmin} />
								)}
							/>
						)}
					</GeneralAdminLink>
				</li>
			</ExpandableNavSection>,
		);
	}

	return items;
}
